import { Component, type ErrorInfo, type ReactNode } from 'react';
import { reportException } from '../analytics/posthog';

interface State {
  failed: boolean;
}

/**
 * Without one of these, React unmounts the whole tree on any render failure and
 * the buyer gets a white page — which is exactly what one missing build
 * variable produced before this existed. A page that says something is broken
 * is worth more than a page that says nothing at all.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // The console is where a developer looks; the screen below is what she sees.
    console.error('Falha ao renderizar', error, info.componentStack);
    // Nobody reports a white page. This is the only way a render failure in
    // production is ever counted.
    reportException(error, { component_stack: info.componentStack });
  }

  override render(): ReactNode {
    if (!this.state.failed) {
      return this.props.children;
    }

    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          background: 'var(--rvk-color-cream-50, #fbf8f4)',
          fontFamily: 'var(--rvk-font-functional, system-ui)'
        }}
      >
        <div style={{ maxWidth: 460, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <h1 style={{ margin: 0, font: '400 26px/1.2 var(--rvk-font-expressive, serif)' }}>
            Alguma coisa quebrou aqui do nosso lado.
          </h1>
          <p style={{ margin: 0, font: '400 14.5px/1.6 var(--rvk-font-functional, system-ui)' }}>
            Recarregue a página. Se continuar assim, escreva para{' '}
            <a href="mailto:acesso@roteirovisk.com">acesso@roteirovisk.com</a>. O seu acesso está
            guardado na sua conta, nada se perde.
          </p>
        </div>
      </div>
    );
  }
}
